import { PriceBoardFlyerEditor } from "@/components/common/section-components/canvas/price-board/PriceBoardFlyerEditor";
import { PriceBoardForm } from "@/components/common/section-components/form/PriceBoardForm";
import { SectionHeader } from "@/components/common/section-components/SectionHeader";

import { AppRoutes } from "@/constants/routes";
import { getGFSection } from "@/services/price-board.service";

import { Flex, useDisclosure } from "@chakra-ui/react";
import { lazy, Suspense, useEffect, useMemo, useState } from "react";

import { useForm, useWatch } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import {
  GeneralFilterData,
  priceBoardCreateUpdateSchema,
} from "../schema/price-board.schema";
import { formatCurrencyInputToNumber, isEmpty } from "@/utils/query-string";
import { zodResolver } from "@hookform/resolvers/zod";
import dayjs from "dayjs";
import {
  createUpdatePricesHandler,
  fillPriceValues,
} from "../schema/handler-functions";
import { useRoles } from "@/hooks/useRoles";

export const PriceBoardCreateUpdatePage = () => {
  const [isActive, setIsActive] = useState<boolean>(false);

  useEffect(() => {
    setIsActive(true);
  }, []);
  const navigate = useNavigate();
  const {
    control,
    formState: { errors },
    setValue,
    getValues,
  } = useForm({
    resolver: zodResolver(priceBoardCreateUpdateSchema),
    defaultValues: {
      fecha: dayjs().format("YYYY-MM-DD"),
    },
  });

  const [loadingPrices, setLoadingPrices] = useState<boolean>(false);
  const [loadingCreateUpdatePrices, setLoadingCreateUpdatePrices] =
    useState<boolean>(false);
  const [gfData, setGFData] = useState<GeneralFilterData | undefined>();

  const handleCancel = () => {
    navigate(AppRoutes.PRICE_BOARD);
  };

  const { open, setOpen } = useDisclosure();

  const { fetchPermissions } = useRoles()

  const handleCreateUpdatePrices = () => {
    createUpdatePricesHandler({
      getValues,
      navigate,
      setLoadingCreateUpdatePrices,
      fetchPermissions,
      setOpen
    });
  };

  const filterDate = useWatch({
    control,
    name: "fecha",
  });

  const fetchGeneralFilterSection = async (showLoad = true) => {
    if (showLoad) setLoadingPrices(true);

    try {
      setGFData(
        await getGFSection({
          fecha: filterDate,
        }),
      );
    } finally {
      setLoadingPrices(false);
    }
  };

  useEffect(() => {
    if (isActive && !isEmpty(filterDate)) fetchGeneralFilterSection();
  }, [isActive, filterDate]);

  useEffect(() => {
    fillPriceValues({
      gfData,
      setValue,
    });
  }, [gfData]);

  const isPastDate = dayjs(filterDate).isBefore(dayjs(), "day");

  const modalTitle = isPastDate ? "Cambiar fecha" : "Confirmar precios";

  const modalSubtitle = isPastDate
    ? "Estarás modificando precios de un día pasado"
    : "¿Desea confirmar estos precios?";

  const watchedValues = useWatch({ control });

  const hasNoFilledValues = useMemo(() => {
    const {
      fecha,
      sojaMercado,
      maizMercado,
      trigoMercado,
      sojaUnion,
      maizUnion,
      trigoUnion,
    } = watchedValues || {};

    return (
      isEmpty(fecha) ||
      isEmpty(sojaMercado) ||
      formatCurrencyInputToNumber(sojaMercado) === 0 ||
      isEmpty(maizMercado) ||
      formatCurrencyInputToNumber(maizMercado) === 0 ||
      isEmpty(trigoMercado) ||
      formatCurrencyInputToNumber(trigoMercado) === 0 ||
      isEmpty(sojaUnion) ||
      formatCurrencyInputToNumber(sojaUnion) === 0 ||
      isEmpty(maizUnion) ||
      formatCurrencyInputToNumber(maizUnion) === 0 ||
      isEmpty(trigoUnion) ||
      formatCurrencyInputToNumber(trigoUnion) === 0
    );
  }, [watchedValues]);

  return (
    <Flex flexDir="column" paddingTop="20px" gap="12px">
      <SectionHeader
        title="Cierre de mercado"
        addButtonLabel="Agregar"
        cancelButtonLabel="Cancelar"
        handleCancel={handleCancel}
        handleAdd={() => setOpen(true)}
        loading={loadingPrices || loadingCreateUpdatePrices}
        disableAdd={hasNoFilledValues}
      />

      {/* Pizarra Flyers */}

      <Flex
        flexDir="column"
        overflowY={"auto"}
        css={{
          "@media (max-width: 1350px) and (min-width: 1272px)": {
            maxHeight: "450px",
          },
          "@media (max-width: 1414px) and (min-width: 1351px)": {
            maxHeight: "560px",
          },
          "@media (max-width: 1590px) and (min-width: 1415px)": {
            maxHeight: "650px",
          },
          "@media (max-width: 1696px) and (min-width: 1591px)": {
            maxHeight: "690px",
          },
          "@media (max-width: 1920px)": {
            maxHeight: "800px",
          },
        }}
      >
        <Flex flexDirection="column">
          <PriceBoardForm
            control={control}
            errors={errors}
            disableInputs={
              isEmpty(filterDate) || loadingPrices || loadingCreateUpdatePrices
            }
          />
          <PriceBoardFlyerEditor control={control} dateData={gfData} />
        </Flex>
      </Flex>
      <Suspense>
        <ConfirmModalDynamic
          openConfirm={open}
          setOpenConfirm={setOpen}
          handleAccept={handleCreateUpdatePrices}
          loadingAccept={loadingCreateUpdatePrices}
          title={modalTitle}
          subtitle={modalSubtitle}
        />
      </Suspense>
    </Flex>
  );
};

const ConfirmModalDynamic = lazy(() =>
  import("@/components/modal/ConfirmModal").then((el) => ({
    default: el["ConfirmModal"],
  })),
);
